import { useState, useMemo } from 'react'
import { Link as LinkIcon, Download, Search, ExternalLink, FileText, Image as ImageIcon, FileSpreadsheet, File as FileIcon, X, FolderOpen, BookOpen, ArrowRight, Pencil } from 'lucide-react'
import { useSDRList, useSDRFacets } from '@/hooks/useSalesDataRoom'
import type { SDRDoc } from '@/hooks/useSalesDataRoom'
import { useAuthStore } from '@/stores/authStore'
import LandingPageEditorModal from '@/components/LandingPageEditorModal'

// Roles autorizadas a editar landing pages:
// marketing (40), sales_rep (50), sales_manager (70), cfo (90), ceo (100), admin
// Mantém compatibilidade com hierarchy_level numérico
const EDITABLE_ROLE_NAMES = new Set([
  'marketing', 'sales_rep', 'sales_manager', 'cfo', 'ceo', 'admin',
])
function canEditLandingPages(user: any): boolean {
  if (!user) return false
  const role = user.role || {}
  const lvl = typeof role.hierarchy_level === 'number' ? role.hierarchy_level : 0
  if (lvl >= 40) return true
  const name = (role.name || '').toLowerCase()
  return EDITABLE_ROLE_NAMES.has(name)
}

/** Extrai slug da LP a partir do external_link `/landingpages/<slug>/` ou `<slug>.html`. */
function extractLpSlug(href: string | null | undefined): string | null {
  if (!href) return null
  const m = href.match(/\/landingpages\/([^/?#]+?)(?:\/?|\.html)?(?:[?#]|$)/i)
  return m ? m[1] : null
}

function fileIcon(type?: string | null) {
  const t = (type || '').toLowerCase()
  if (t === 'pdf') return FileText
  if (t === 'png' || t === 'jpg' || t === 'jpeg' || t === 'webp') return ImageIcon
  if (t === 'xlsx' || t === 'xls' || t === 'csv') return FileSpreadsheet
  if (t === 'doc' || t === 'docx') return FileText
  return FileIcon
}

function fileTypeAccent(type?: string | null) {
  const t = (type || '').toLowerCase()
  if (t === 'pdf') return 'text-rose-600 bg-rose-100 dark:text-rose-300 dark:bg-rose-900/30'
  if (['png', 'jpg', 'jpeg', 'webp'].includes(t)) return 'text-violet-600 bg-violet-100 dark:text-violet-300 dark:bg-violet-900/30'
  if (['xlsx', 'xls', 'csv'].includes(t)) return 'text-emerald-600 bg-emerald-100 dark:text-emerald-300 dark:bg-emerald-900/30'
  if (['doc', 'docx'].includes(t)) return 'text-blue-600 bg-blue-100 dark:text-blue-300 dark:bg-blue-900/30'
  return 'text-gray-600 bg-gray-100 dark:text-gray-300 dark:bg-gray-800'
}

function bytesToHuman(b?: number | null) {
  if (!b) return ''
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`
  return `${(b / (1024 * 1024)).toFixed(1)} MB`
}

function CopyLinkButton({ href }: { href: string }) {
  const [copied, setCopied] = useState(false)
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(href)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch { /* ignore */ }
  }
  return (
    <button
      type="button"
      onClick={onCopy}
      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 h-9 rounded-lg text-xs font-semibold border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
      aria-label="Copiar link"
    >
      <LinkIcon className="h-3.5 w-3.5" />
      {copied ? 'Copiado!' : 'Copiar Link'}
    </button>
  )
}

function DocCard({ doc, canEdit, onEdit }: {
  doc: SDRDoc
  canEdit: boolean
  onEdit: (slug: string) => void
}) {
  const Icon = fileIcon(doc.file_type)
  const accent = fileTypeAccent(doc.file_type)
  const url = doc.external_link || doc.file_path || '#'
  const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`
  const isPdf = (doc.file_type || '').toLowerCase() === 'pdf'
  const lpSlug = extractLpSlug(doc.external_link)
  const showEdit = canEdit && !!lpSlug

  return (
    <article className="group relative flex flex-col rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:shadow-xl hover:-translate-y-0.5 hover:border-blue-300 dark:hover:border-blue-700 transition-all duration-200 overflow-hidden">
      {/* Top accent bar */}
      <div className="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 opacity-70 group-hover:opacity-100 transition-opacity" />

      <div className="flex-1 p-5 flex flex-col gap-3">
        {/* Icon + tags */}
        <div className="flex items-start justify-between gap-2">
          <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl ${accent}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex flex-wrap gap-1.5 justify-end">
            {doc.ncc_module && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[10px] font-bold uppercase tracking-wider">
                {doc.ncc_module}
              </span>
            )}
            {doc.regulatory_framework && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[10px] font-bold uppercase tracking-wider">
                {doc.regulatory_framework}
              </span>
            )}
          </div>
        </div>

        {/* Title */}
        <h3 className="text-base font-bold text-gray-900 dark:text-white leading-snug line-clamp-2">
          {doc.title}
        </h3>

        {/* Description */}
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-3 flex-1">
          {doc.description}
        </p>

        {/* File meta */}
        {(doc.file_type || doc.file_size) && (
          <div className="text-[11px] text-gray-400 dark:text-gray-500 font-mono uppercase tracking-wider flex items-center gap-2">
            {doc.file_type && <span>.{doc.file_type}</span>}
            {doc.file_size ? <span>· {bytesToHuman(doc.file_size)}</span> : null}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="px-5 py-3.5 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/40 flex items-center gap-2">
        <CopyLinkButton href={fullUrl} />
        {showEdit && (
          <button
            type="button"
            onClick={() => onEdit(lpSlug!)}
            className="inline-flex items-center justify-center gap-1.5 px-3 h-9 rounded-lg text-xs font-semibold border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
            aria-label="Editar textos da landing page"
            title="Editar textos da Landing Page (Marketing/Comercial+)"
          >
            <Pencil className="h-3.5 w-3.5" />
            Editar
          </button>
        )}
        {isPdf && doc.file_path ? (
          <a
            href={doc.file_path}
            download={doc.file_name || true}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 h-9 rounded-lg text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white transition-colors"
            aria-label="Baixar PDF"
          >
            <Download className="h-3.5 w-3.5" />
            Download
          </a>
        ) : (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 h-9 rounded-lg text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white transition-colors"
            aria-label="Abrir documento"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Abrir
          </a>
        )}
      </div>
    </article>
  )
}

export default function SalesDataRoomPage() {
  const user = useAuthStore((s) => s.user)
  const canEdit = canEditLandingPages(user)
  const [editingSlug, setEditingSlug] = useState<string | null>(null)

  const [q, setQ] = useState('')
  const [nccModule, setNccModule] = useState<string>('')
  const [regulatory, setRegulatory] = useState<string>('')

  const filters = useMemo(
    () => ({
      q: q.trim() || undefined,
      ncc_module: nccModule || undefined,
      regulatory_framework: regulatory || undefined,
    }),
    [q, nccModule, regulatory]
  )

  const { data: docs, isLoading } = useSDRList(filters)
  const { data: facets } = useSDRFacets()

  const hasFilters = !!(q || nccModule || regulatory)
  const clearFilters = () => {
    setQ(''); setNccModule(''); setRegulatory('')
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <header className="bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 dark:from-black dark:via-slate-900 dark:to-blue-950 text-white">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-sm items-center justify-center flex-shrink-0">
              <FolderOpen className="h-7 w-7 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-2">Sales Data Room</p>
              <h1 className="text-3xl font-bold leading-tight mb-2">Repositório Comercial</h1>
              <p className="text-blue-100/80 text-sm max-w-2xl leading-relaxed">
                Apresentações, dashboards e análises da plataforma Nai Compliance Center, organizados por módulo e framework regulatório.
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Sticky filter bar */}
      <div className="sticky top-0 z-20 bg-white/95 dark:bg-gray-950/95 backdrop-blur-md border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-col lg:flex-row gap-3">
            {/* Keyword search */}
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar por título ou descrição..."
                className="w-full h-11 pl-10 pr-9 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                aria-label="Buscar documento"
              />
              {q && (
                <button
                  type="button"
                  onClick={() => setQ('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                  aria-label="Limpar busca"
                >
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              )}
            </div>

            {/* NCC Module */}
            <select
              value={nccModule}
              onChange={(e) => setNccModule(e.target.value)}
              className="h-11 px-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all min-w-[12rem]"
              aria-label="Filtrar por módulo NCC"
            >
              <option value="">Todos os módulos NCC</option>
              {facets?.ncc_modules.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>

            {/* Regulatory Framework */}
            <select
              value={regulatory}
              onChange={(e) => setRegulatory(e.target.value)}
              className="h-11 px-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all min-w-[14rem]"
              aria-label="Filtrar por framework regulatório"
            >
              <option value="">Todos os frameworks</option>
              {facets?.regulatory_frameworks.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>

            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="h-11 px-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-1.5"
              >
                <X className="h-4 w-4" /> Limpar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Playbook Comercial — sempre visivel no topo */}
        <a
          href="https://social.myncc.com.br/portfolio/playbook-comercial-nai.html"
          target="_blank"
          rel="noopener noreferrer"
          className="group relative block mb-6 overflow-hidden rounded-2xl border border-blue-200 dark:border-blue-900/60 bg-gradient-to-br from-[#00284a] via-[#005092] to-[#0a1628] hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
        >
          <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(0,212,170,0.25) 0%, transparent 60%)' }} />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-5 p-6 sm:p-7">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-[#00d4aa]/15 border border-[#00d4aa]/30 text-[#00d4aa] flex-shrink-0">
              <BookOpen className="h-7 w-7" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#00d4aa]/15 text-[#00d4aa] text-[10px] font-bold uppercase tracking-wider border border-[#00d4aa]/30">
                  Playbook Comercial
                </span>
                <span className="text-[10px] font-mono uppercase tracking-wider text-blue-200/70">v3.0 · Maio 2026</span>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white leading-snug">
                Playbook Comercial NAI-IT
              </h3>
              <p className="text-sm text-blue-100/80 mt-1 leading-relaxed">
                Pilares, ICP, pipeline do CRM, metodologias, objecoes, cases reais e disciplina diaria — tudo o que o vendedor precisa para evoluir de iniciante a senior.
              </p>
            </div>
            <div className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg bg-[#00d4aa] text-[#0a1628] text-sm font-bold group-hover:bg-[#00e8ba] transition-colors flex-shrink-0">
              Abrir
              <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </div>
        </a>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-72 rounded-2xl bg-gray-100 dark:bg-gray-900 animate-pulse" />
            ))}
          </div>
        ) : !docs || docs.length === 0 ? (
          <div className="text-center py-24">
            <FolderOpen className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-700 mb-4" />
            <h3 className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-1">Nenhum documento encontrado</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {hasFilters ? 'Tente ajustar os filtros ou' : 'Comece'} cadastrando documentos em Configurações → Sales Data Room.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-5">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                <span className="font-semibold text-gray-900 dark:text-white">{docs.length}</span> documento{docs.length === 1 ? '' : 's'}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {docs.map((doc) => (
                <DocCard key={doc.id} doc={doc} canEdit={canEdit} onEdit={setEditingSlug} />
              ))}
            </div>
          </>
        )}
      </main>

      {editingSlug && (
        <LandingPageEditorModal
          slug={editingSlug}
          onClose={() => setEditingSlug(null)}
        />
      )}
    </div>
  )
}
